import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  AlertTriangle,
  Archive,
  ArchiveRestore,
  Banknote,
  CalendarDays,
  ChevronUp,
  Download,
  Droplet,
  Eye,
  FileText,
  Hash,
  MoreHorizontal,
  Pencil,
  RefreshCw,
  Scan,
  Truck,
  User,
} from 'lucide-react';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';
import {
  Drawer,
  DrawerContent,
  DrawerDescription,
  DrawerHeader,
  DrawerTitle,
} from '../ui/drawer';
import { useEstMobile } from '../../hooks/useEstMobile';
import { nomLisible } from '../../lib/libelles';
import { AuthBlob } from './fichierAuthentifie';
import {
  CHAMPS_MONTANT,
  DEAD_END_STATES,
  controleChamp,
  descriptionLisible,
  extensionDe,
  formatDate,
  formatOcrValue,
  getOcrState,
  nomChamp,
  ocrFieldEntries,
  parseCustomLabel,
  typeDocument,
} from './lectureDocument';

// Couleur de l'anneau de lecture: vert fiable, orange à vérifier, rouge
// impossible, bleu pendant la lecture.
const OCR_COULEURS = {
  reliable: 'text-success',
  manual: 'text-success',
  pending: 'text-primary',
  review: 'text-flame-ink',
  failed: 'text-destructive',
  pdf_unreadable: 'text-destructive',
  file_missing: 'text-destructive',
};

// La pastille de format dit « ceci est un PDF », pas « ce document va mal »:
// elle puise dans l'échelle catégorielle (`--chart-*`), jamais dans
// `destructive` ou `success`, qui veulent dire quelque chose.
const PASTILLES = {
  PDF: 'bg-chart-5 text-white',
  JPG: 'bg-chart-2 text-white',
  PNG: 'bg-chart-1 text-white',
  WEBP: 'bg-chart-1 text-white',
};
const pastilleDe = (extension) => PASTILLES[extension] || 'bg-muted-foreground text-background';

const iconeChamp = (cle) => {
  const k = String(cle || '');
  if (k.includes('date')) return CalendarDays;
  if (k === 'plate') return Truck;
  if (k.includes('volume')) return Droplet;
  if (CHAMPS_MONTANT.has(k)) return Banknote;
  if (k.includes('name')) return User;
  if (/number|nif|rccm|siret/.test(k)) return Hash;
  return FileText;
};

/* ------------------------------------------------------------------------ */

// Page dessinée quand aucun aperçu réel n'est disponible (PDF, fichier non
// chargé): la pastille du format et le type en petites capitales.
function PageIllustree({ extension, libelleType, mini = false }) {
  return (
    <span className={'absolute inset-0 flex flex-col ' + (mini ? 'gap-1 p-1' : 'gap-1 p-1.5')}>
      <span className={'w-fit shrink-0 rounded px-1 py-0.5 font-bold leading-none tracking-wide ' + (mini ? 'text-[7px]' : 'text-[9px]') + ' ' + pastilleDe(extension)}>
        {extension}
      </span>
      {!mini && (
        <span className="line-clamp-2 shrink-0 text-[6.5px] font-semibold uppercase leading-tight tracking-wider text-muted-foreground dark:text-background/55">
          {libelleType}
        </span>
      )}
      <span className="h-0.5 w-3/4 shrink-0 rounded bg-muted-foreground/25 dark:bg-background/25" />
      <span className="h-0.5 w-2/3 shrink-0 rounded bg-muted-foreground/20 dark:bg-background/20" />
      <span className="mt-auto h-0.5 w-1/2 shrink-0 rounded bg-muted-foreground/20 dark:bg-background/20" />
    </span>
  );
}

function Apercu({ doc, libelleType, mini = false }) {
  const extension = extensionDe(doc);
  const isImage = (doc.file_type || '').startsWith('image/');
  const illustration = <PageIllustree extension={extension} libelleType={libelleType} mini={mini} />;
  return (
    <span className="relative block aspect-[3/4] w-full overflow-hidden rounded-md border border-border/60 bg-card shadow-sm dark:border-transparent dark:bg-foreground/90">
      {isImage && doc.url ? (
        <AuthBlob
          url={doc.url}
          className="absolute inset-0 h-full w-full object-cover"
          fallback={illustration}
          errorFallback={illustration}
        />
      ) : (
        illustration
      )}
    </span>
  );
}

/* ------------------------------------------------------------------------ */

const RAYON = 40;
const CIRCONFERENCE = 2 * Math.PI * RAYON;

// Anneau de lecture: la part du cercle tracée suit le score. Il se dessine
// à l'arrivée (transition coupée si l'utilisateur réduit les animations).
function AnneauLecture({ etat, score, libelle, compact = false }) {
  const [trace, setTrace] = useState(false);
  useEffect(() => {
    const id = window.requestAnimationFrame(() => setTrace(true));
    return () => window.cancelAnimationFrame(id);
  }, []);

  const enCours = etat.key === 'pending';
  const manuel = etat.key === 'manual';
  const part = enCours ? 0.25 : manuel ? 1 : Math.max(0, Math.min(1, (score ?? 0) / 100));
  const decalage = CIRCONFERENCE * (1 - (trace || enCours ? part : 0));

  let grand = score != null ? `${score} %` : '…';
  let sousTitre = 'Texte extrait';
  if (manuel) {
    grand = 'Saisie';
    sousTitre = 'Saisie à la main';
  } else if (enCours) {
    sousTitre = 'Lecture en cours';
  } else if (DEAD_END_STATES.has(etat.key)) {
    sousTitre = 'Lecture impossible';
  }

  return (
    <div
      data-testid="document-lecture-auto"
      role="img"
      aria-label={libelle}
      title={libelle}
      className={
        (compact ? 'flex items-center gap-1.5 ' : 'flex flex-col items-center gap-0.5 text-center ') +
        (OCR_COULEURS[etat.key] || 'text-flame-ink')
      }
    >
      <span className={'relative flex shrink-0 items-center justify-center ' + (compact ? 'h-10 w-10' : 'h-14 w-14')} aria-hidden="true">
        <svg
          viewBox="0 0 96 96"
          className={
            'absolute inset-0 h-full w-full -rotate-90 dark:[filter:drop-shadow(0_0_3px_currentColor)]' +
            (enCours ? ' animate-spin [animation-duration:1.6s] motion-reduce:animate-none' : '')
          }
        >
          <circle cx="48" cy="48" r={RAYON} fill="none" stroke="currentColor" strokeWidth="8" className="opacity-15" />
          <circle
            data-testid="anneau-trace"
            cx="48"
            cy="48"
            r={RAYON}
            fill="none"
            stroke="currentColor"
            strokeWidth="8"
            strokeLinecap="round"
            strokeDasharray={CIRCONFERENCE.toFixed(2)}
            strokeDashoffset={decalage.toFixed(2)}
            className="transition-[stroke-dashoffset] duration-700 ease-out motion-reduce:transition-none"
          />
        </svg>
        {compact ? (
          // Sur téléphone, le pourcentage tient dans l'anneau.
          <span className="relative text-[10px] font-bold leading-none tabular-nums">{score != null ? `${score}%` : manuel ? '✓' : '…'}</span>
        ) : (
          <>
            <Scan className="h-6 w-6" strokeWidth={1.75} />
            <span className="absolute inset-0 flex items-center justify-center text-[7px] font-bold tracking-wide">OCR</span>
          </>
        )}
      </span>
      {!compact && (
        <span className="flex flex-col items-center" aria-hidden="true">
          <span className="text-lg font-bold leading-none tabular-nums">{grand}</span>
          <span className="mt-0.5 text-[10px] leading-tight text-muted-foreground">{sousTitre}</span>
        </span>
      )}
    </div>
  );
}

/* ------------------------------------------------------------------------ */

// Les consignes du bandeau: celles du serveur, puis l'alerte de chaque champ
// douteux (« Camion (plaque) : Plaque inconnue de la flotte »).
const consignesDe = (doc) => {
  const liste = [...(doc.ocr_guidance?.next_steps || [])];
  const sources = doc.ocr_field_sources || {};
  Object.entries(doc.ocr_field_checks || {}).forEach(([cle, controle]) => {
    if (controle?.status === 'review' && controle.reason && sources[cle] !== 'manual') {
      liste.push(`${nomChamp(cle)} : ${controle.reason}`);
    }
  });
  return [...new Set(liste.filter(Boolean))];
};

function BandeauLecture({ etat, guidance, consignes }) {
  const impossible = DEAD_END_STATES.has(etat.key);
  const ton = impossible
    ? { cadre: 'border-destructive/40 bg-destructive/10', texte: 'text-destructive', puce: 'bg-destructive' }
    : { cadre: 'border-flame/40 bg-flame/10', texte: 'text-flame-ink', puce: 'bg-flame' };
  return (
    <div
      data-testid="document-bandeau"
      data-ton={impossible ? 'impossible' : 'a-verifier'}
      className={'flex flex-wrap items-baseline gap-x-2 gap-y-0.5 rounded-lg border px-2.5 py-1.5 text-xs leading-snug ' + ton.cadre}
    >
      {/* Titre, explication et consignes s'enchaînent sur la même ligne et ne
          passent à la suivante que faute de place. */}
      <AlertTriangle className={'h-3.5 w-3.5 shrink-0 self-center ' + ton.texte} aria-hidden="true" />
      <span className={'font-semibold ' + ton.texte}>{guidance?.title || etat.label}</span>
      {guidance?.reason ? <span className="min-w-0 break-words text-foreground/80">{guidance.reason}</span> : null}
      {consignes.length > 0 && (
        <ul className="contents text-foreground/80">
          {consignes.map((consigne) => (
            <li key={consigne} className="flex min-w-0 items-baseline gap-1.5 text-foreground/80">
              <span className={'h-1 w-1 shrink-0 -translate-y-0.5 rounded-full ' + ton.puce} aria-hidden="true" />
              <span className="min-w-0 break-words">{consigne}</span>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function TuileChamp({ etat, icone: Icone, libelle, valeur, raison }) {
  const styles = {
    lu: { cadre: 'border-border/70 bg-background/50', icone: 'text-primary', valeur: 'text-foreground' },
    manuel: { cadre: 'border-success/40 bg-success/10', icone: 'text-success', valeur: 'text-foreground' },
    verifier: { cadre: 'border-flame/50 bg-flame/10', icone: 'text-flame-ink', valeur: 'text-flame-ink' },
    completer: { cadre: 'border-flame/50 bg-flame/10', icone: 'text-flame-ink', valeur: 'text-flame-ink' },
  }[etat];
  return (
    <div
      data-testid="document-champ"
      data-etat={etat}
      title={raison || `${libelle} : ${valeur}`}
      className={'flex min-w-0 items-center gap-2 rounded-lg border px-2 py-1.5 ' + styles.cadre}
    >
      <Icone className={'h-4 w-4 shrink-0 ' + styles.icone} aria-hidden="true" />
      <span className="flex min-w-0 flex-wrap items-baseline gap-x-1.5 leading-tight">
        <span className={'text-[11px] ' + (etat === 'completer' ? 'font-medium text-flame-ink' : 'text-muted-foreground')}>{libelle}</span>
        <span className={'min-w-0 max-w-full break-words text-xs font-medium lg:truncate ' + styles.valeur}>{valeur}</span>
      </span>
    </div>
  );
}

/* ------------------------------------------------------------------------ */

// Tout ce qu'on calcule une fois pour les deux présentations (carte large,
// résumé mobile + volet).
function lireDocument(doc, canSeeMoney) {
  const { label: customDocLabel, base: nomFichier } = parseCustomLabel(doc.file_name);
  const etat = getOcrState(doc);
  const texte = etat.text || '';
  const manuel = etat.key === 'manual';
  const score = doc.confidence_score != null && !manuel ? Math.round(doc.confidence_score * 100) : null;
  const consignes = consignesDe(doc);
  const champs = ocrFieldEntries(doc, { canSeeMoney });
  const manquants = doc.ocr_missing_fields || [];
  return {
    nomFichier,
    etat,
    texte,
    score,
    libelleLecture: `${etat.label}${score != null ? ` : ${score} %` : ''}`,
    champsSaisie: doc.ocr_manual_fields || [],
    sources: doc.ocr_field_sources || {},
    impasse: DEAD_END_STATES.has(etat.key),
    champs,
    manquants,
    extrait: descriptionLisible(texte),
    libelleType: customDocLabel || typeDocument(doc.doc_type),
    consignes,
    bandeauVisible:
      !['reliable', 'manual', 'pending'].includes(etat.key) && Boolean(doc.ocr_guidance || consignes.length),
  };
}

function Actions({
  doc,
  lu,
  manualOpen,
  archiving,
  archive = false,
  unarchiving = false,
  ouvrir,
  onDownload,
  onArchive,
  onUnarchive,
  onManualEntry,
  grand = false,
  className = '',
}) {
  const taille = grand ? 'default' : 'sm';
  return (
    <div data-testid="document-actions" className={className}>
      {!archive && lu.champsSaisie.length > 0 && lu.etat.key !== 'pending' && (
        <Button
          type="button"
          size={taille}
          variant="outline"
          className={'gap-1.5 ' + (lu.impasse ? 'border-primary text-primary' : 'border-primary/50')}
          onClick={() => onManualEntry?.(doc)}
        >
          <Pencil className="h-3.5 w-3.5" />
          {manualOpen ? 'Fermer la saisie' : 'Saisir à la main'}
        </Button>
      )}
      {doc.url && (
        <Button type="button" size={taille} variant="outline" className="gap-1.5" onClick={ouvrir}>
          <Eye className="h-3.5 w-3.5" />
          Voir
        </Button>
      )}
      {doc.url && (
        <Button type="button" size={taille} className="gap-1.5" onClick={() => onDownload?.(doc)}>
          <Download className="h-3.5 w-3.5" />
          Télécharger
        </Button>
      )}
      {archive ? (
        <Button
          type="button"
          size={taille}
          variant="outline"
          className="gap-1.5 border-primary text-primary"
          disabled={unarchiving}
          onClick={() => onUnarchive?.(doc)}
        >
          <ArchiveRestore className="h-3.5 w-3.5" />
          Désarchiver
        </Button>
      ) : (
        <Button
          type="button"
          size={taille}
          variant="outline"
          className="gap-1.5 hover:text-flame-ink"
          disabled={archiving}
          onClick={() => onArchive?.(doc)}
        >
          <Archive className="h-3.5 w-3.5" />
          Archiver
        </Button>
      )}
    </div>
  );
}

// Quand, par qui et pourquoi le document a quitté les listes. Sans date
// d'archivage (document actif), rien.
function LigneArchivage({ doc, className = '' }) {
  if (!doc.archived_at) return null;
  const auteur = doc.archived_by ? nomLisible(doc.archived_by) : '';
  const motif = String(doc.archive_reason || '').trim();
  return (
    <p
      data-testid="document-archivage"
      className={'flex min-w-0 items-center gap-1 text-xs text-muted-foreground ' + className}
      title={motif || undefined}
    >
      <Archive className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
      <span className="truncate">
        Archivé le {formatDate(doc.archived_at)}
        {auteur && auteur !== 'Sans nom' ? ` par ${auteur}` : ''}
        {motif ? ` · ${motif}` : ''}
      </span>
    </p>
  );
}

function BlocDroite({ doc, lu, resumeTrajet, ouvrir, grand = false, className = '' }) {
  return (
    <div data-testid="document-card-droite" className={'flex min-w-0 flex-col gap-1 text-sm ' + className}>
      <p className="flex min-w-0 items-center gap-1.5">
        <Truck className="h-4 w-4 shrink-0 text-primary" aria-hidden="true" />
        <span className="shrink-0 text-xs text-muted-foreground">Trajet</span>
        {doc.associated_trip_id ? (
          <Link
            to={`/trips/${doc.associated_trip_id}`}
            className={'block min-w-0 truncate font-semibold text-primary hover:underline' + (grand ? ' py-2.5' : '')}
          >
            {resumeTrajet?.(doc.associated_trip_id) || 'Trajet concerné'}
          </Link>
        ) : (
          <span className="truncate text-muted-foreground">Aucun trajet rattaché</span>
        )}
      </p>
      <div className="border-t border-border/60" />
      <p
        data-testid="document-extrait"
        title={lu.extrait || undefined}
        className={'line-clamp-2 break-words leading-snug xl:line-clamp-3 ' + (lu.extrait ? 'font-medium text-foreground' : 'text-muted-foreground')}
      >
        <FileText className="mr-1.5 inline h-4 w-4 -translate-y-px text-primary" aria-hidden="true" />
        <span className="text-xs font-normal text-muted-foreground">Extrait du document : </span>
        {lu.extrait || 'Aucun texte extrait'}
      </p>
      {lu.texte ? (
        <button
          type="button"
          onClick={ouvrir}
          className={'mt-auto self-start whitespace-nowrap text-left text-xs font-medium text-primary hover:underline' + (grand ? ' min-h-10 py-2' : '')}
        >
          Voir tout le texte extrait <span aria-hidden="true">→</span>
        </button>
      ) : null}
    </div>
  );
}

function Tuiles({ doc, lu, className = '' }) {
  if (!lu.champs.length && !lu.manquants.length) return null;
  return (
    <div data-testid="document-champs" className={'grid gap-1.5 ' + className}>
      {lu.champs.map(([cle, valeur]) => {
        const controle = controleChamp(doc, cle);
        const saisi = lu.sources[cle] === 'manual';
        const aVerifier = !saisi && controle?.status === 'review';
        return (
          <TuileChamp
            key={cle}
            etat={saisi ? 'manuel' : aVerifier ? 'verifier' : 'lu'}
            icone={saisi ? Pencil : iconeChamp(cle)}
            libelle={nomChamp(cle)}
            valeur={`${formatOcrValue(valeur)}${aVerifier ? ' · à vérifier' : ''}`}
            raison={controle?.reason}
          />
        );
      })}
      {lu.manquants.map((cle) => (
        <TuileChamp key={`manque-${cle}`} etat="completer" icone={FileText} libelle="À compléter" valeur={nomChamp(cle)} />
      ))}
    </div>
  );
}

function MenuAutres({ doc, lu, ouvrir, onRetry, archive = false }) {
  // Un document archivé ne se relit pas: seule la lecture de son texte reste.
  if (archive && !lu.texte) return null;
  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button type="button" variant="ghost" size="sm" className="h-7 w-7 p-0 md:h-7" aria-label="Autres actions">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        {archive ? null : (
          <DropdownMenuItem disabled={lu.etat.key === 'pending'} onSelect={() => onRetry?.(doc)}>
            <RefreshCw className="mr-2 h-3.5 w-3.5" />
            Relancer la lecture
          </DropdownMenuItem>
        )}
        {lu.texte ? (
          <DropdownMenuItem onSelect={ouvrir}>
            <Eye className="mr-2 h-3.5 w-3.5" />
            Voir tout le texte extrait
          </DropdownMenuItem>
        ) : null}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}

const CADRE_CARTE =
  'relative rounded-xl border border-primary/25 bg-card bg-gradient-to-br from-primary/[0.07] via-transparent to-primary/[0.03] shadow-[0_0_0_1px_hsl(var(--primary)/0.06),0_10px_30px_-18px_hsl(var(--primary)/0.55)] transition-colors hover:border-primary/45';

/* ------------------------------------------------------------------------ */

// Téléphone: la carte ne garde que l'essentiel; le reste s'ouvre dans un
// volet du bas au toucher de la carte ou par le bouton « Détails ».
function CarteMobile(props) {
  const { doc, lu, children, footer, onPreview, onRetry, archive = false } = props;
  const [ouvert, setOuvert] = useState(false);
  const panneau = useRef(null);
  const boutonDetails = useRef(null);
  // L'aperçu est une autre fenêtre: le volet se ferme d'abord pour ne pas
  // empiler deux fenêtres modales.
  const ouvrir = () => {
    setOuvert(false);
    onPreview?.(doc);
  };
  const aVoir = lu.bandeauVisible || lu.manquants.length > 0;
  return (
    <>
      <article
        data-testid="document-card"
        data-presentation="resume"
        onClick={() => setOuvert(true)}
        className={CADRE_CARTE + ' flex cursor-pointer items-center gap-2 p-2 min-[400px]:gap-2.5 min-[400px]:p-2.5'}
      >
        <span className="w-9 shrink-0 min-[400px]:w-10" aria-hidden="true">
          <Apercu doc={doc} libelleType={lu.libelleType} mini />
        </span>
        <div className="min-w-0 flex-1">
          <p className="truncate text-sm font-bold text-foreground">{lu.nomFichier}</p>
          <p className="truncate text-xs text-muted-foreground">
            {lu.libelleType} · {formatDate(doc.created_at)}
          </p>
          {archive && <LigneArchivage doc={doc} />}
          {aVoir && (
            <p
              data-testid="document-a-verifier"
              className={'mt-0.5 flex items-center gap-1 whitespace-nowrap text-[11px] font-medium ' + (lu.impasse ? 'text-destructive' : 'text-flame-ink')}
            >
              <AlertTriangle className="h-3 w-3" aria-hidden="true" />
              {lu.impasse ? 'Lecture impossible' : 'À vérifier'}
            </p>
          )}
        </div>
        <AnneauLecture etat={lu.etat} score={lu.score} libelle={lu.libelleLecture} compact />
        <Button
          type="button"
          variant="ghost"
          size="sm"
          ref={boutonDetails}
          className="h-10 w-10 shrink-0 gap-1 px-0 text-xs min-[400px]:w-auto min-[400px]:px-2 md:h-10"
          aria-label={`Détails de ${lu.nomFichier}`}
          onClick={(e) => {
            e.stopPropagation();
            setOuvert(true);
          }}
        >
          <span className="hidden min-[400px]:inline">Détails</span>
          <ChevronUp className="h-3.5 w-3.5" aria-hidden="true" />
        </Button>
      </article>

      <Drawer open={ouvert} onOpenChange={setOuvert} shouldScaleBackground={false}>
        <DrawerContent
          ref={panneau}
          tabIndex={-1}
          data-testid="document-volet"
          className="max-h-[85vh] bg-card focus:outline-none"
          // Le focus entre dans le volet à l'ouverture et revient au bouton
          // « Détails » à la fermeture.
          onOpenAutoFocus={(e) => {
            e.preventDefault();
            panneau.current?.focus({ preventScroll: true });
          }}
          onCloseAutoFocus={(e) => {
            e.preventDefault();
            boutonDetails.current?.focus({ preventScroll: true });
          }}
        >
          <DrawerHeader className="flex items-start gap-3 px-4 pb-2 pt-3 text-left">
            <span className="w-12 shrink-0" aria-hidden="true">
              <Apercu doc={doc} libelleType={lu.libelleType} mini />
            </span>
            <div className="min-w-0 flex-1 space-y-1">
              <DrawerTitle className="break-words text-base leading-snug">{lu.nomFichier}</DrawerTitle>
              <DrawerDescription className="text-xs">
                {lu.libelleType} · {formatDate(doc.created_at)}
              </DrawerDescription>
              {archive && <LigneArchivage doc={doc} />}
            </div>
          </DrawerHeader>
          <div className="space-y-3 overflow-y-auto overscroll-contain px-4 pb-6">
            <Actions {...props} ouvrir={ouvrir} grand className="grid grid-cols-2 gap-2" />
            <div className="grid grid-cols-[4.5rem_minmax(0,1fr)] items-start gap-3 rounded-lg border border-border/60 bg-background/40 p-2.5">
              <AnneauLecture etat={lu.etat} score={lu.score} libelle={lu.libelleLecture} />
              <BlocDroite doc={doc} lu={lu} resumeTrajet={props.resumeTrajet} ouvrir={ouvrir} grand />
            </div>
            {lu.bandeauVisible && <BandeauLecture etat={lu.etat} guidance={doc.ocr_guidance} consignes={lu.consignes} />}
            {children}
            <Tuiles doc={doc} lu={lu} className="grid-cols-2" />
            {footer}
            {archive ? null : (
              <Button
                type="button"
                variant="ghost"
                size="sm"
                className="w-full gap-1.5 text-muted-foreground"
                disabled={lu.etat.key === 'pending'}
                onClick={() => onRetry?.(doc)}
              >
                <RefreshCw className="h-3.5 w-3.5" />
                Relancer la lecture
              </Button>
            )}
          </div>
        </DrawerContent>
      </Drawer>
    </>
  );
}

// Écran large: tout sur la carte, sans zone vide. ≥ 1280 px sur une rangée
// (vignette, nom et actions, anneau, trajet et extrait), puis le bandeau et
// les tuiles sur toute la largeur; entre 768 et
// 1279 px l'anneau se loge sous la vignette, à côté du trajet.
function CarteLarge(props) {
  const { doc, lu, children, footer, onPreview, onRetry, resumeTrajet, archive = false } = props;
  const ouvrir = () => onPreview?.(doc);
  return (
    <article data-testid="document-card" data-presentation="complete" className={CADRE_CARTE + ' p-3'}>
      <div className="absolute right-1.5 top-1.5">
        <MenuAutres doc={doc} lu={lu} ouvrir={ouvrir} onRetry={onRetry} archive={archive} />
      </div>

      <div className="grid grid-cols-[4rem_minmax(0,1fr)] gap-x-3 gap-y-1.5 xl:grid-cols-[4rem_minmax(0,1fr)_auto_minmax(13rem,0.6fr)] 2xl:grid-cols-[4rem_minmax(0,1fr)_auto_minmax(15rem,0.8fr)] xl:gap-x-4">
        <button
          type="button"
          aria-label={`Aperçu de ${lu.nomFichier}`}
          onClick={ouvrir}
          className="col-start-1 row-start-1 self-start rounded-md focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        >
          <Apercu doc={doc} libelleType={lu.libelleType} />
        </button>

        <div data-testid="document-card-gauche" className="col-start-2 row-start-1 flex min-w-0 flex-col justify-center gap-1.5 pr-7 xl:pr-0">
          <button
            type="button"
            onClick={ouvrir}
            title={lu.nomFichier}
            className="block max-w-full truncate text-left text-base font-bold leading-tight text-foreground hover:underline"
          >
            {lu.nomFichier}
          </button>
          <div className="flex flex-wrap items-center gap-x-3 gap-y-1.5">
            <span className="inline-flex max-w-full items-center gap-1 rounded-md border border-primary/25 bg-primary/10 px-1.5 py-0.5 text-xs font-medium text-foreground">
              <FileText className="h-3.5 w-3.5 shrink-0 text-primary" aria-hidden="true" />
              <span className="truncate">{lu.libelleType}</span>
            </span>
            <span className="flex items-center gap-1 text-xs text-muted-foreground">
              <CalendarDays className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {formatDate(doc.created_at)}
            </span>
            <Actions {...props} ouvrir={ouvrir} className="flex flex-wrap gap-1.5" />
          </div>
          {archive && <LigneArchivage doc={doc} />}
        </div>

        <div className="col-start-1 row-start-2 flex justify-center self-start xl:col-start-3 xl:row-start-1 xl:items-center xl:self-stretch xl:border-l xl:border-border/60 xl:pl-4">
          <AnneauLecture etat={lu.etat} score={lu.score} libelle={lu.libelleLecture} />
        </div>

        <BlocDroite
          doc={doc}
          lu={lu}
          resumeTrajet={resumeTrajet}
          ouvrir={ouvrir}
          className="col-start-2 row-start-2 xl:col-start-4 xl:row-start-1 xl:border-l xl:border-border/60 xl:pl-4 xl:pr-6"
        />

        {lu.bandeauVisible && (
          <div className="col-span-full col-start-1">
            <BandeauLecture etat={lu.etat} guidance={doc.ocr_guidance} consignes={lu.consignes} />
          </div>
        )}

        <Tuiles
          doc={doc}
          lu={lu}
          className="col-span-full col-start-1 grid-cols-3 lg:grid-cols-[repeat(auto-fit,minmax(10rem,1fr))]"
        />
      </div>

      {children}
      {footer}
    </article>
  );
}

/**
 * Carte d'un document: vignette, nom et actions, anneau de lecture, trajet et
 * extrait; un bandeau quand la lecture n'est pas complète; les champs lus en
 * tuiles. Sur téléphone, un résumé et un volet du bas.
 * `children` reçoit le formulaire de saisie manuelle.
 *
 * `archive`: la carte d'un document archivé. Elle dit quand, par qui et
 * pourquoi il a quitté les listes, et propose « Désarchiver » (`onUnarchive`)
 * à la place d'« Archiver »; ni saisie à la main ni nouvelle lecture.
 */
export default function CarteDocument({ doc, canSeeMoney = true, ...props }) {
  const mobile = useEstMobile();
  const lu = lireDocument(doc, canSeeMoney);
  return mobile ? <CarteMobile doc={doc} lu={lu} {...props} /> : <CarteLarge doc={doc} lu={lu} {...props} />;
}
